// src/App.js
// ForestMood / Mindful Steps — Lehigh University
// Routes: 4 variants (flat/hilly × 10min/20min) from Campus Walkability Index data.
// Terrain preference: Flat / Mixed / Hilly — selected by user before walk.

import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { db } from './firebase';
import { collection, addDoc } from 'firebase/firestore';
import Admin from './Admin';
import { selectRoute } from './routes';

// ─── Constants ───────────────────────────────────────────────────────────────

const EMOTIONS = [
  { label: 'Calm',      color: '#4A90D9', description: 'Gentle, sheltered paths' },
  { label: 'Energized', color: '#F5A623', description: 'Longer, varied circuit' },
  { label: 'Anxious',   color: '#9B59B6', description: 'Short, open paths' },
  { label: 'Low',       color: '#7B68EE', description: 'Quiet, green paths' },
  { label: 'Happy',     color: '#7ED321', description: 'Scenic winding loop' },
];

const DURATIONS = [
  { label: '10 min', value: 10 },
  { label: '20 min', value: 20 },
];

// Terrain options — mapped to AC4 gradient preference from walkability data
const TERRAINS = [
  {
    value: 'flat',
    label: 'Flat',
    icon: '〰️',
    description: 'Level paths, easy on joints',
    color: '#7ED321',
  },
  {
    value: 'mixed',
    label: 'Mixed',
    icon: '〜',
    description: 'Gentle slopes, some variety',
    color: '#F5A623',
  },
  {
    value: 'hilly',
    label: 'Hilly',
    icon: '⛰️',
    description: 'Elevation changes, more effort',
    color: '#D0021B',
  },
];

// Breathing phases for meditation overlay (seconds)
const BREATHING_CYCLE = [
  { label: 'Breathe In',  duration: 4, color: '#4A90D9' },
  { label: 'Hold',        duration: 4, color: '#7ED321' },
  { label: 'Breathe Out', duration: 6, color: '#9B59B6' },
  { label: 'Rest',        duration: 2, color: '#F5A623' },
];
const BREATHING_TOTAL = BREATHING_CYCLE.reduce((s, p) => s + p.duration, 0);

const MEDITATION_PROMPTS = [
  'Close your eyes and feel the ground beneath your feet.',
  'Notice the sounds of the forest around you.',
  'With each breath, let your shoulders drop.',
  'Feel the air on your skin. You are here, now.',
  'Observe any thoughts — then gently let them pass.',
  'Let the greenery around you fill your senses.',
];

const ORS_API_KEY = process.env.REACT_APP_ORS_API_KEY;

// ─── ORS Route Fetch ──────────────────────────────────────────────────────────
// Accepts either pre-defined waypoints (from route.orsWaypoints)
// or computes waypoints from GPS position + emotion + terrain.
async function fetchRouteORS({ fixedWaypoints, start, emotion, durationMin, terrain }) {
  let waypoints;

  if (fixedWaypoints && fixedWaypoints.length > 0) {
    // Use curated Lehigh waypoints from routes.js (hilly south campus route)
    waypoints = fixedWaypoints;
  } else {
    // Compute waypoints from GPS position
    // Terrain affects: radius (how far out) and angle pattern (hilly = asymmetric)
    const r =
      terrain === 'hilly' ? (durationMin === 10 ? 0.006 : 0.010) :
      terrain === 'mixed' ? (durationMin === 10 ? 0.005 : 0.009) :
      /* flat */            (durationMin === 10 ? 0.004 : 0.008);

    const angles =
      emotion.label === 'Energized' ? [0, 60, 120, 180, 240, 300, 360] :
      emotion.label === 'Happy'     ? [0, 72, 144, 216, 288, 360] :
      terrain === 'hilly'           ? [0, 45, 90, 180, 270, 360] :
                                      [0, 90, 180, 270, 360];

    waypoints = angles.map(angle => {
      const rad = (angle * Math.PI) / 180;
      // Hilly: bias toward south (negative lat offset) where campus drops in elevation
      const latBias = terrain === 'hilly' ? -0.002 : 0;
      return [
        start[1] + r * Math.sin(rad),
        start[0] + r * Math.cos(rad) + latBias,
      ];
    });
    waypoints.unshift([start[1], start[0]]);
    waypoints.push([start[1], start[0]]);
  }

  const response = await fetch(
    'https://api.openrouteservice.org/v2/directions/foot-walking/geojson',
    {
      method: 'POST',
      headers: {
        Authorization: ORS_API_KEY,
        'Content-Type': 'application/json',
        Accept: 'application/json, application/geo+json',
      },
      body: JSON.stringify({
        coordinates: waypoints,
        preference: terrain === 'hilly' ? 'shortest' : 'recommended',
        options: {
          profile_params: {
            weightings: {
              green: terrain === 'hilly' ? 0.5 : 0.8,
            },
          },
        },
      }),
    }
  );

  const data = await response.json();
  if (!data.features || !data.features[0]) throw new Error('No ORS route found');
  return data.features[0].geometry.coordinates.map(c => [c[1], c[0]]);
}

// ─── Location Tracker ─────────────────────────────────────────────────────────
function LocationTracker({ setPath, setCurrentPos, tracking }) {
  const map = useMap();
  const markerRef = useRef(null);

  useEffect(() => {
    if (!tracking) return;
    const watchId = navigator.geolocation.watchPosition(
      position => {
        const pos = [position.coords.latitude, position.coords.longitude];
        setCurrentPos(pos);
        setPath(prev => [...prev, pos]);
        map.setView(pos, 17);
        if (markerRef.current) {
          markerRef.current.setLatLng(pos);
        } else {
          const L = window.L || require('leaflet');
          const icon = L.divIcon({
            className: '',
            html: `<div style="width:20px;height:20px;background:#4A90D9;border:3px solid white;
                   border-radius:50%;animation:fmpulse 2s infinite;"></div>
                   <style>@keyframes fmpulse{
                     0%{box-shadow:0 0 0 0 rgba(74,144,217,.6)}
                     70%{box-shadow:0 0 0 12px rgba(74,144,217,0)}
                     100%{box-shadow:0 0 0 0 rgba(74,144,217,0)}}</style>`,
            iconSize: [20, 20], iconAnchor: [10, 10],
          });
          markerRef.current = L.marker(pos, { icon }).addTo(map);
        }
      },
      err => console.error('GPS:', err),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 30000 }
    );
    return () => {
      navigator.geolocation.clearWatch(watchId);
      if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
    };
  }, [tracking, map, setPath, setCurrentPos]);
  return null;
}

// ─── Meditation Overlay ───────────────────────────────────────────────────────
function MeditationOverlay({ onFinish, durationSec = 300 }) {
  const [elapsed, setElapsed] = useState(0);
  const [promptIdx, setPromptIdx] = useState(0);

  const cyclePos = elapsed % BREATHING_TOTAL;
  let acc = 0, phase = BREATHING_CYCLE[0];
  for (const p of BREATHING_CYCLE) {
    if (cyclePos < acc + p.duration) { phase = p; break; }
    acc += p.duration;
  }
  const phaseProgress = (cyclePos - acc) / phase.duration;

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(prev => {
        if (prev >= durationSec) { clearInterval(interval); onFinish(); return prev; }
        if (prev > 0 && prev % 45 === 0) setPromptIdx(i => (i + 1) % MEDITATION_PROMPTS.length);
        return prev + 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [durationSec, onFinish]);

  const remaining = durationSec - elapsed;
  const progress = elapsed / durationSec;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(10,25,10,0.96)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: 'sans-serif', color: 'white', padding: 32, textAlign: 'center',
    }}>
      <p style={{ fontSize: 11, letterSpacing: 3, opacity: 0.5, marginBottom: 6 }}>🌿 FOREST MEDITATION</p>
      <div style={{
        width: 120 + phaseProgress * 80, height: 120 + phaseProgress * 80,
        borderRadius: '50%', background: phase.color + '22',
        border: `3px solid ${phase.color}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.6s ease', marginBottom: 28,
      }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: phase.color }}>{phase.label}</span>
      </div>
      <p style={{ fontSize: 17, lineHeight: 1.7, maxWidth: 260, marginBottom: 28, opacity: 0.9 }}>
        {MEDITATION_PROMPTS[promptIdx]}
      </p>
      <p style={{ fontSize: 40, fontWeight: 700, marginBottom: 4 }}>
        {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, '0')}
      </p>
      <p style={{ fontSize: 11, opacity: 0.4, marginBottom: 6 }}>remaining</p>
      <div style={{ width: 200, height: 3, background: 'rgba(255,255,255,0.1)', borderRadius: 2, marginBottom: 32 }}>
        <div style={{
          width: `${progress * 100}%`, height: '100%',
          background: '#7ED321', borderRadius: 2, transition: 'width 1s linear',
        }} />
      </div>
      <button onClick={onFinish} style={{
        padding: '11px 28px', borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.25)',
        background: 'transparent', color: 'rgba(255,255,255,0.7)',
        fontSize: 14, cursor: 'pointer',
      }}>
        End Early &amp; Resume Walk
      </button>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
function App() {
  const [screen, setScreen] = useState('setup');

  // Setup selections
  const [selectedEmotion, setSelectedEmotion]   = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [selectedTerrain, setSelectedTerrain]   = useState('flat'); // NEW
  const [meditationMode, setMeditationMode]     = useState(false);

  // Walk state
  const [path, setPath]                         = useState([]);
  const [emotionLogs, setEmotionLogs]           = useState([]);
  const [showEmotionCheck, setShowEmotionCheck] = useState(false);
  const [currentPos, setCurrentPos]             = useState(null);
  const [tracking, setTracking]                 = useState(false);
  const [initialPos, setInitialPos]             = useState(null);
  const [suggestedRoute, setSuggestedRoute]     = useState(null);
  const [meditationSpot, setMeditationSpot]     = useState(null);
  const [loadingRoute, setLoadingRoute]         = useState(false);
  const [routeError, setRouteError]             = useState(null);
  const [elapsedTime, setElapsedTime]           = useState(0);
  const [activeRouteName, setActiveRouteName]   = useState(''); // NEW

  // Meditation
  const [showMeditation, setShowMeditation]           = useState(false);
  const [meditationTriggered, setMeditationTriggered] = useState(false);
  const [meditationCompletedSec, setMeditationCompletedSec] = useState(0);

  // Music
  const [musicOn, setMusicOn] = useState(false);
  const audioRef = useRef(null);

  // Summary
  const [feedback, setFeedback]       = useState('');
  const [finalEmotion, setFinalEmotion] = useState(null);
  const [saved, setSaved]             = useState(false);

  // ── Initial GPS ──
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      pos => setInitialPos([pos.coords.latitude, pos.coords.longitude]),
      () => setInitialPos([40.60724, -75.37828]),
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 }
    );
  }, []);

  // ── Elapsed timer ──
  useEffect(() => {
    if (!tracking) return;
    const t = setInterval(() => setElapsedTime(p => p + 1), 1000);
    return () => clearInterval(t);
  }, [tracking]);

  // ── Mood check every 10 min ──
  useEffect(() => {
    if (!tracking) return;
    const t = setInterval(() => setShowEmotionCheck(true), 600000);
    return () => clearInterval(t);
  }, [tracking]);

  // ── Auto-dismiss mood check after 1 min ──
  useEffect(() => {
    if (!showEmotionCheck) return;
    const t = setTimeout(() => {
      if (currentPos) setEmotionLogs(p => [...p, {
        pos: currentPos,
        emotion: { label: 'No response', color: '#999' },
      }]);
      setShowEmotionCheck(false);
    }, 60000);
    return () => clearTimeout(t);
  }, [showEmotionCheck, currentPos]);

  // ── Meditation trigger at 10 min of walking ──
  useEffect(() => {
    if (!tracking || !meditationMode || meditationTriggered) return;
    if (elapsedTime >= 600) {
      setMeditationTriggered(true);
      setTracking(false);
      setShowMeditation(true);
    }
  }, [elapsedTime, tracking, meditationMode, meditationTriggered]);

  // ── Music ──
  useEffect(() => {
    if (!audioRef.current) return;
    if (musicOn) audioRef.current.play().catch(() => {});
    else audioRef.current.pause();
  }, [musicOn]);

  if (window.location.pathname === '/admin') return <Admin />;

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleStartWalk = async () => {
    if (!selectedEmotion || !selectedDuration || !initialPos) return;
    setLoadingRoute(true);
    setRouteError(null);

    try {
      // 1. Find best curated route for this emotion + duration + terrain
      const curated = selectRoute(
        selectedEmotion.label,
        selectedDuration.value,
        selectedTerrain
      );

      if (curated && curated.coordinates && curated.coordinates.length > 2) {
        // ── Case A: fully-traced curated route (CSV-derived) ──
        setSuggestedRoute(curated.coordinates);
        if (curated.meditationSpot) setMeditationSpot(curated.meditationSpot);
        setActiveRouteName(curated.name);

      } else if (curated && curated.orsWaypoints) {
        // ── Case B: curated ORS waypoints (hilly south campus) ──
        const route = await fetchRouteORS({ fixedWaypoints: curated.orsWaypoints });
        setSuggestedRoute(route);
        if (curated.meditationSpot) setMeditationSpot(curated.meditationSpot);
        setActiveRouteName(curated.name);

      } else {
        // ── Case C: full ORS fallback (no curated match) ──
        const route = await fetchRouteORS({
          start: initialPos,
          emotion: selectedEmotion,
          durationMin: selectedDuration.value,
          terrain: selectedTerrain,
        });
        setSuggestedRoute(route);
        setMeditationSpot(route[Math.floor(route.length / 2)]);
        setActiveRouteName('Custom Route');
      }

      setScreen('map');
      setTracking(true);
      setElapsedTime(0);
      setMeditationTriggered(false);
      setMeditationCompletedSec(0);

    } catch (err) {
      console.error('Route error:', err);
      setRouteError('Could not generate route. Check your connection and try again.');
    } finally {
      setLoadingRoute(false);
    }
  };

  const handleMeditationFinish = () => {
    setMeditationCompletedSec(300);
    setShowMeditation(false);
    setTracking(true);
  };

  const handleStopWalk = () => {
    setTracking(false);
    setMusicOn(false);
    setScreen('summary');
  };

  const handleEmotionCheck = emotion => {
    if (currentPos) setEmotionLogs(p => [...p, { pos: currentPos, emotion }]);
    setShowEmotionCheck(false);
  };

  const handleSaveWalk = async () => {
    try {
      await addDoc(collection(db, 'walks'), {
        startEmotion: selectedEmotion.label,
        finalEmotion: finalEmotion ? finalEmotion.label : null,
        terrain: selectedTerrain,
        routeName: activeRouteName,
        duration: elapsedTime,
        meditationDurationSec: meditationCompletedSec,
        meditationUsed: meditationMode,
        emotionLogs: emotionLogs.map(e => e.emotion.label),
        feedback: feedback.slice(0, 2000),
        timestamp: new Date(),
      });
      setSaved(true);
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  const resetApp = () => {
    setScreen('setup');
    setPath([]); setEmotionLogs([]);
    setSelectedEmotion(null); setSelectedDuration(null);
    setSelectedTerrain('flat');
    setSuggestedRoute(null); setMeditationSpot(null);
    setElapsedTime(0); setFeedback('');
    setFinalEmotion(null); setSaved(false);
    setShowMeditation(false); setMeditationTriggered(false);
    setMeditationMode(false); setMusicOn(false);
    setMeditationCompletedSec(0); setActiveRouteName('');
  };

  const formatTime = sec =>
    `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

  // ── Which terrain option is selected ──
  const activeTerrain = TERRAINS.find(t => t.value === selectedTerrain) || TERRAINS[0];

  // ─── GPS loading ───────────────────────────────────────────────────────────
  if (!initialPos) return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      height: '100vh', fontFamily: 'sans-serif', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ fontSize: 32 }}>🌿</div>
      <div style={{ color: '#888' }}>Locating you...</div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // SCREEN: Setup
  // ═══════════════════════════════════════════════════════════════════════════
  if (screen === 'setup') return (
    <div style={{
      padding: 24, fontFamily: 'sans-serif', maxWidth: 400,
      margin: '0 auto', paddingBottom: 48,
    }}>
      <h2 style={{ textAlign: 'center', marginBottom: 4 }}>🌿 Mindful Steps</h2>
      <p style={{ textAlign: 'center', color: '#888', marginBottom: 28, fontSize: 14 }}>
        A mindful walk through Lehigh's forest paths
      </p>

      {/* ── Emotion selector ── */}
      <h3 style={{ marginBottom: 12 }}>How are you feeling?</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
        {EMOTIONS.map(e => (
          <button key={e.label} onClick={() => setSelectedEmotion(e)} style={{
            padding: '12px 16px', borderRadius: 10,
            border: `2px solid ${selectedEmotion?.label === e.label ? e.color : '#eee'}`,
            background: selectedEmotion?.label === e.label ? e.color : 'white',
            color: selectedEmotion?.label === e.label ? 'white' : '#333',
            cursor: 'pointer', textAlign: 'left', fontSize: 15, transition: 'all 0.2s',
          }}>
            <strong>{e.label}</strong> — <span style={{ opacity: 0.85 }}>{e.description}</span>
          </button>
        ))}
      </div>

      {/* ── Duration selector ── */}
      <h3 style={{ marginBottom: 6 }}>Walk duration</h3>
      <p style={{ fontSize: 12, color: '#aaa', marginBottom: 12 }}>
        {meditationMode
          ? '10 min walk + 5 min meditation = ~15 min total  |  20 min walk + 5 min = ~25 min total'
          : 'How long would you like to walk?'}
      </p>
      <div style={{ display: 'flex', gap: 10, marginBottom: 28 }}>
        {DURATIONS.map(d => (
          <button key={d.label} onClick={() => setSelectedDuration(d)} style={{
            flex: 1, padding: '14px 0', borderRadius: 10,
            border: `2px solid ${selectedDuration?.value === d.value ? '#333' : '#eee'}`,
            background: selectedDuration?.value === d.value ? '#333' : 'white',
            color: selectedDuration?.value === d.value ? 'white' : '#333',
            cursor: 'pointer', fontSize: 15, fontWeight: 'bold', transition: 'all 0.2s',
          }}>
            {d.label}
            {meditationMode && (
              <div style={{ fontSize: 11, fontWeight: 'normal', opacity: 0.7, marginTop: 2 }}>
                +5 min meditation
              </div>
            )}
          </button>
        ))}
      </div>

      {/* ── Terrain selector ── NEW ── */}
      <h3 style={{ marginBottom: 6 }}>Terrain preference</h3>
      <p style={{ fontSize: 12, color: '#aaa', marginBottom: 12 }}>
        How hilly would you like the route to be?
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
        {TERRAINS.map(t => {
          const isActive = selectedTerrain === t.value;
          return (
            <button
              key={t.value}
              onClick={() => setSelectedTerrain(t.value)}
              style={{
                flex: 1, padding: '12px 6px', borderRadius: 10,
                border: `2px solid ${isActive ? t.color : '#eee'}`,
                background: isActive ? t.color : 'white',
                color: isActive ? 'white' : '#555',
                cursor: 'pointer', transition: 'all 0.2s',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 4,
              }}
            >
              <span style={{ fontSize: 20 }}>{t.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 'bold' }}>{t.label}</span>
              <span style={{
                fontSize: 10, opacity: isActive ? 0.9 : 0.6,
                lineHeight: 1.3, textAlign: 'center',
              }}>
                {t.description}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Meditation toggle ── */}
      <div
        onClick={() => setMeditationMode(m => !m)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px', borderRadius: 12,
          border: `2px solid ${meditationMode ? '#7ED321' : '#eee'}`,
          background: meditationMode ? '#f0fff0' : 'white',
          marginBottom: 32, cursor: 'pointer', transition: 'all 0.2s',
        }}
      >
        <div>
          <strong style={{ fontSize: 15 }}>🧘 Include Meditation</strong>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: '#888' }}>
            After 10 min of walking, pause for 5 min of guided breathing
          </p>
        </div>
        <div style={{
          width: 44, height: 24, borderRadius: 12, flexShrink: 0, marginLeft: 12,
          background: meditationMode ? '#7ED321' : '#ccc',
          position: 'relative', transition: 'background 0.2s',
        }}>
          <div style={{
            width: 20, height: 20, borderRadius: '50%', background: 'white',
            position: 'absolute', top: 2, left: meditationMode ? 22 : 2,
            transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
          }} />
        </div>
      </div>

      {routeError && (
        <p style={{ color: '#D0021B', textAlign: 'center', marginBottom: 12, fontSize: 14 }}>
          {routeError}
        </p>
      )}

      {/* ── Start button ── */}
      <button
        onClick={handleStartWalk}
        disabled={!selectedEmotion || !selectedDuration || loadingRoute}
        style={{
          width: '100%', padding: '16px 0', borderRadius: 12, border: 'none',
          background: selectedEmotion && selectedDuration
            ? (meditationMode ? '#7ED321' : selectedEmotion.color)
            : '#ccc',
          color: 'white', fontSize: 17, fontWeight: 'bold',
          cursor: selectedEmotion && selectedDuration ? 'pointer' : 'not-allowed',
          transition: 'all 0.2s',
        }}
      >
        {loadingRoute ? 'Finding your route...'
          : meditationMode ? '🧘 Start Walk + Meditation →'
          : `Start ${activeTerrain.icon} Walk →`}
      </button>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // SCREEN: Map
  // ═══════════════════════════════════════════════════════════════════════════
  if (screen === 'map') return (
    <div style={{ height: '100vh', width: '100vw', position: 'relative', fontFamily: 'sans-serif' }}>

      {/* Ambient audio — replace src with /forest.mp3 once downloaded */}
      <audio ref={audioRef} loop preload="none" src="/forest.mp3" style={{ display: 'none' }} />

      <MapContainer center={initialPos} zoom={17} style={{ height: '100%', width: '100%' }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <LocationTracker setPath={setPath} setCurrentPos={setCurrentPos} tracking={tracking} />

        {suggestedRoute && (
          <Polyline
            positions={suggestedRoute}
            color={selectedEmotion.color}
            weight={5} opacity={0.9} dashArray="10 6"
          />
        )}
        {path.length > 1 && (
          <Polyline positions={path} color="#222" weight={3} opacity={0.7} />
        )}
        {meditationMode && meditationSpot && (() => {
          const L = window.L || require('leaflet');
          const icon = L.divIcon({
            className: '',
            html: '<div style="font-size:26px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3))">🧘</div>',
            iconSize: [28, 28], iconAnchor: [14, 14],
          });
          return <Marker position={meditationSpot} icon={icon} />;
        })()}
        {emotionLogs.map((e, i) => (
          <CircleMarker key={i} center={e.pos} radius={10}
            fillColor={e.emotion.color} color="white" weight={2} fillOpacity={1} />
        ))}
      </MapContainer>

      {/* Status bar */}
      <div style={{
        position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 1000, background: 'white', padding: '8px 16px', borderRadius: 20,
        boxShadow: '0 2px 10px rgba(0,0,0,0.15)', fontSize: 13, fontWeight: 600,
        color: '#333', display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap',
      }}>
        <span>⏱ {formatTime(elapsedTime)}</span>
        <span style={{ opacity: 0.3 }}>|</span>
        <span>{activeTerrain.icon} {activeRouteName}</span>
        {meditationMode && !meditationTriggered && (
          <>
            <span style={{ opacity: 0.3 }}>|</span>
            <span style={{ fontSize: 11, color: '#7ED321' }}>
              🧘 {Math.max(0, Math.ceil((600 - elapsedTime) / 60))}m
            </span>
          </>
        )}
      </div>

      {/* Music button */}
      <button onClick={() => setMusicOn(m => !m)} style={{
        position: 'fixed', top: 16, right: 16, zIndex: 1000,
        width: 44, height: 44, borderRadius: '50%', border: 'none',
        background: musicOn ? '#4A90D9' : 'white',
        color: musicOn ? 'white' : '#333',
        fontSize: 18, cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {musicOn ? '🔇' : '🎵'}
      </button>

      {/* End Walk */}
      <button onClick={handleStopWalk} style={{
        position: 'fixed', bottom: 40, left: '50%', transform: 'translateX(-50%)',
        zIndex: 9999, padding: '16px 48px', fontSize: 17,
        background: '#D0021B', color: 'white', border: 'none', borderRadius: 12,
        cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.4)', fontWeight: 'bold',
        WebkitAppearance: 'none',
      }}>
        End Walk
      </button>

      {/* Mood check popup */}
      {showEmotionCheck && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', zIndex: 1000,
          background: 'white', padding: 24, borderRadius: 14,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)', textAlign: 'center', minWidth: 220,
        }}>
          <p style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 12 }}>
            How are you feeling now?
          </p>
          {EMOTIONS.map(e => (
            <button key={e.label} onClick={() => handleEmotionCheck(e)} style={{
              display: 'block', width: '100%', margin: '6px 0', padding: '11px',
              background: e.color, color: 'white', border: 'none', borderRadius: 8,
              cursor: 'pointer', fontSize: 14, fontWeight: 'bold',
            }}>
              {e.label}
            </button>
          ))}
        </div>
      )}

      {/* Meditation overlay */}
      {showMeditation && (
        <MeditationOverlay onFinish={handleMeditationFinish} durationSec={300} />
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // SCREEN: Summary
  // ═══════════════════════════════════════════════════════════════════════════
  if (screen === 'summary') return (
    <div style={{
      padding: 24, fontFamily: 'sans-serif', maxWidth: 400,
      margin: '0 auto', height: '100vh', overflowY: 'scroll',
    }}>
      <h2 style={{ textAlign: 'center' }}>🌿 Walk Complete</h2>

      <p style={{ textAlign: 'center', color: '#555', marginBottom: 4 }}>
        Walk time: <strong>{formatTime(elapsedTime)}</strong>
      </p>
      {meditationCompletedSec > 0 && (
        <p style={{ textAlign: 'center', color: '#7ED321', marginBottom: 4, fontSize: 14 }}>
          🧘 Meditation: <strong>{formatTime(meditationCompletedSec)}</strong>
        </p>
      )}
      <p style={{ textAlign: 'center', color: '#555', marginBottom: 4 }}>
        Route: <strong>{activeRouteName}</strong> {activeTerrain.icon}
      </p>
      <p style={{ textAlign: 'center', color: '#555', marginBottom: 24 }}>
        Started feeling{' '}
        <strong style={{ color: selectedEmotion.color }}>{selectedEmotion.label}</strong>
      </p>

      {emotionLogs.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ marginBottom: 10 }}>Mood during your walk:</h4>
          {emotionLogs.map((e, i) => (
            <div key={i} style={{
              padding: '10px 14px', margin: '6px 0', borderRadius: 8,
              background: e.emotion.color, color: 'white', fontSize: 14, fontWeight: 'bold',
            }}>
              Check-in {i + 1}: {e.emotion.label}
            </div>
          ))}
        </div>
      )}

      <h4 style={{ marginBottom: 10 }}>How do you feel now?</h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
        {EMOTIONS.map(e => (
          <button key={e.label} onClick={() => setFinalEmotion(e)} style={{
            padding: '10px 14px', borderRadius: 8,
            border: `2px solid ${finalEmotion?.label === e.label ? e.color : '#eee'}`,
            background: finalEmotion?.label === e.label ? e.color : 'white',
            color: finalEmotion?.label === e.label ? 'white' : '#333',
            cursor: 'pointer', fontSize: 14, fontWeight: 'bold',
          }}>
            {e.label}
          </button>
        ))}
      </div>

      <h4 style={{ marginBottom: 10 }}>Any feedback?</h4>
      <textarea
        value={feedback}
        onChange={e => setFeedback(e.target.value.slice(0, 2000))}
        placeholder="How was your walk? Any thoughts..."
        maxLength={2000}
        style={{
          width: '100%', height: 100, padding: 12, borderRadius: 8,
          border: '2px solid #eee', fontSize: 14, resize: 'none',
          marginBottom: 16, boxSizing: 'border-box',
        }}
      />

      {saved ? (
        <p style={{ textAlign: 'center', color: '#7ED321', fontWeight: 'bold', marginBottom: 16 }}>
          ✓ Walk saved
        </p>
      ) : (
        <button onClick={handleSaveWalk} style={{
          width: '100%', padding: '14px 0', borderRadius: 12, border: 'none',
          background: selectedEmotion.color, color: 'white',
          fontSize: 16, fontWeight: 'bold', cursor: 'pointer', marginBottom: 12,
        }}>
          Save Walk
        </button>
      )}

      <button onClick={resetApp} style={{
        width: '100%', padding: '14px 0', borderRadius: 12, border: 'none',
        background: '#333', color: 'white', fontSize: 16, fontWeight: 'bold', cursor: 'pointer',
      }}>
        Start New Walk
      </button>
    </div>
  );
}

export default App;